import { Bug, Gauge, Sprout, Users, TrendingUp, Mail, FilePen, ClipboardList, Car, UserCheck, UserMinus, Building2, Home, CalendarDays, CalendarOff, KanbanSquare, Link2, CalendarCheck, MailPlus, BarChart3, MessagesSquare, Ticket, Inbox, Bot, FlaskConical, Banknote, ClipboardCheck, Network, ChartPie, CalendarRange, Send } from 'lucide-react';
import type { SpaceConfig, SpaceNavGroup } from '../../shared';
import PeopleSpace from './PeopleSpace';
import {
  CANDIDATE_COMMS_VIEW,
  INTERNAL_PLAYGROUND_VIEW,
  JOBAUTO_USE,
  ONBOARDING_VIEW,
  ORG_PLANNING_OVERVIEW_ACCESS,
  PEOPLE_COCKPIT_VIEW,
  REFERENCE_LETTERS_DRAFT,
} from './permissions';

export const peopleSpaceConfig: SpaceConfig = {
  id: 'people-space',
  name: 'People Space',
  shortName: 'PS',
  icon: Users,
  basePath: '/',
  component: PeopleSpace,
  theme: 'light',
  fullWidth: true,
  allowedDomains: ['finn.com'],
  navItems: [
    {
      id: 'home',
      name: 'Home',
      icon: Home,
      path: '/',
      position: 'main',
    },
    {
      // Confidential-by-default like Absence Insights: every employee's ticket, grouped by DRI,
      // not just the viewer's own — hidden from the sidebar unless the ticket-management-viewers
      // group grants space.people.ticket_management.view.
      id: 'ticket-management',
      name: 'Ticket Management',
      icon: Ticket,
      path: '/ticket-management',
      position: 'main',
      group: 'boris',
      requiredPermission: 'space.people.ticket_management.view',
    },
    {
      // Every employee (People Tickets cutover, Oct 2026): their own requests, never anyone else's
      // — the proxy decides that from the verified token (routes/people-tickets.js).
      id: 'my-inquiries',
      name: 'My inquiries',
      icon: Inbox,
      path: '/my-inquiries',
      position: 'main',
      group: 'boris',
    },
    {
      // The merged People request form (People Tickets), for every employee since the cutover
      // (Oct 2026). The ONE way in for BP, Payroll & Benefits and Office requests.
      id: 'submit-request',
      name: 'New request',
      icon: MailPlus,
      path: '/submit-request',
      position: 'main',
      group: 'boris',
    },
    {
      // Every employee (Oct 8 2026): policy answers from the People Confluence. The proxy route
      // never checked a permission; the beta gate on the page and here is gone with this.
      id: 'chat-with-boris',
      name: 'Chat with Boris',
      icon: Bot,
      path: '/chat-with-boris',
      position: 'main',
      group: 'boris',
    },
    {
      id: 'ta-cockpit',
      name: 'TA Cockpit',
      icon: TrendingUp,
      path: '/ta-cockpit',
      position: 'main',
      group: 'talent-acquisition',
    },
    {
      // Talent Community (Oct 2026; was "Talent Pool & Community" until the Oct 16 rename): Vetted
      // Talent, Leads, Internal Talents. The id and path stay `talent-pool` so links keep working.
      // Gated since the Vetted Talent import (Oct 21 2026) put real candidates on it: same
      // permission as the route guard in PeopleSpace.tsx and the /api/lever proxy prefix.
      id: 'talent-pool',
      name: 'Talent Community',
      icon: Sprout,
      path: '/talent-pool',
      position: 'main',
      group: 'talent-acquisition',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      id: 'ta-action-boards',
      name: 'TA Action Boards',
      icon: KanbanSquare,
      path: '/ta-action-boards',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'candidate-communication',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      id: 'interview-links',
      name: 'Interview Links',
      icon: Link2,
      path: '/interview-links',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'candidate-communication',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      id: 'interviews',
      // Renamed to "Log": the page is no longer only booked interviews — it also
      // records every candidate email the board sent. The PATH stays /interviews
      // so existing bookmarks keep working.
      name: 'Log',
      icon: CalendarCheck,
      path: '/interviews',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'candidate-communication',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      id: 'email-library',
      name: 'Email Library',
      icon: MailPlus,
      path: '/email-library',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'candidate-communication',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      // Cora, the CV screening assistant (moved here from Make + Relevance AI, Oct 2026). Her
      // screenings are assessments of candidates' CVs, so the same audience as the boards.
      id: 'cora',
      name: 'Cora Screening',
      icon: Bot,
      path: '/cora',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'candidate-communication',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      // Its own subgroup, Talent Sourcing, since Oct 19 2026 (Sofia); since Oct 23 2026 (Sofia) that
      // folder sits INSIDE Candidate Communication (`parent` on the subgroup below), last in it, so
      // a click on Talent Sourcing opens Outreach and the InMail Library. Same permission as
      // before: the proxy routes sit under /api/ta-action-board/outreach, inside the
      // candidateComms() guard.
      id: 'outreach',
      name: 'Outreach',
      icon: Send,
      path: '/outreach',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'talent-sourcing',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      // The InMail Library (Sofia, Oct 19 2026): writing rules, a sample draft and the outreach
      // templates — the Email Library page in its outreach variant. Same permission as the Library.
      id: 'outreach-library',
      name: 'InMail Library',
      icon: MailPlus,
      path: '/outreach-library',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'talent-sourcing',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      // Moved out of People Operations and under Candidate Communication, last in that
      // subgroup (Sofia, Sep 11 2026): what it measures is this block — drafts, replies,
      // interviews booked, cancelled and moved — so it belongs beside them rather than
      // beside the equity calculator.
      //
      // Deliberately still NOT gated with requiredPermission, unlike every other item in this
      // subgroup. The data route enforces `space.people.usage_analytics.view` itself and the view
      // explains a 403 — whereas hiding the item would make an unreachable board
      // indistinguishable from a missing one, for a board whose whole job is telling you whether
      // tracking still works. ⚠️ Consequence: this is the one item that renders the Candidate
      // Communication header for a colleague outside `cca.peoplespace@finn.com`, who will see the
      // subgroup with Usage Analytics alone inside it. Gating it would hide the board from the
      // People Operations colleagues who can read it today.
      id: 'usage-analytics',
      name: 'Usage Analytics',
      icon: BarChart3,
      path: '/usage-analytics',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'product-experience',
    },
    {
      // Moved out of People & Org Planning (Theresa, Sep 23 2026): the reports are about THIS
      // product and are worked by the TA team, so the page belongs beside the board that measures
      // the same thing rather than beside the headcount plan.
      //
      // ⚠️ The permission changed with it, from INTERNAL_PLAYGROUND_VIEW to CANDIDATE_COMMS_VIEW,
      // and that was the point rather than a side effect: the org-planning audience is the role
      // approvers plus a per-user allowlist, which did NOT include Sofia — an owner of the board
      // could not open it — while it DID include the CEO and CFO, who have no reason to triage bug
      // reports. `cca.peoplespace@finn.com` is the people who file these reports, and TA manages
      // that membership in Google Groups with no PR. Still stricter than the feedback BUTTON,
      // which is open to everyone at FINN: the reports carry reporter addresses and quoted
      // candidate messages. The proxy re-authorizes per request — see
      // `proxy-server/routes/feedback-triage.js`.
      id: 'feedback-triage',
      name: 'Feedback Triage',
      icon: Bug,
      path: '/feedback-triage',
      position: 'main',
      group: 'talent-acquisition',
      subgroup: 'product-experience',
      requiredPermission: CANDIDATE_COMMS_VIEW,
    },
    {
      id: 'equity-calculator',
      name: 'Equity Calculator',
      icon: TrendingUp,
      path: '/equity-calculator',
      position: 'main',
      group: 'finnies',
    },
    {
      id: 'salary-bands',
      name: 'Salary Bands',
      icon: Banknote,
      path: '/salary-bands',
      position: 'main',
      group: 'finnies',
    },
    {
      // Every employee (Oct 12 2026): supervisors request a letter here instead of in Airtable. The
      // proxy decides whom each caller may request one for (their reporting line; BP: anyone).
      id: 'reference-letter-request',
      name: 'Request a reference letter',
      icon: FilePen,
      path: '/request-reference-letter',
      position: 'main',
      group: 'offboarding',
    },
    {
      id: 'reference-letters',
      name: 'Reference Letters',
      icon: Mail,
      path: '/reference-letters',
      position: 'main',
      group: 'offboarding',
      requiredPermission: REFERENCE_LETTERS_DRAFT,
    },
    {
      id: 'manager-review',
      name: 'Manager Review',
      icon: ClipboardList,
      path: '/manager-review',
      position: 'main',
      group: 'finnies',
    },
    {
      id: 'jobauto-calculator',
      name: 'JobAuto Rechner',
      icon: Car,
      path: '/jobauto-calculator',
      position: 'main',
      group: 'people-operations',
      // Same permission the proxy enforces on /api/jobauto (PLA-626) — hidden from everyone else.
      requiredPermission: JOBAUTO_USE,
    },
    {
      id: 'onboarding-scheduling',
      name: 'Scheduling',
      icon: CalendarDays,
      path: '/onboarding-scheduling',
      position: 'main',
      group: 'onboarding',
      // Same permission the proxy enforces on /api/onboarding (PLA-626) — hidden from everyone
      // else. It is the only item in the Onboarding group, so the whole group hides for anyone
      // without the permission.
      requiredPermission: ONBOARDING_VIEW,
    },
    {
      // Confidential. requiredPermission hides this from everyone except members
      // of the absence-insights-viewers group (see App.tsx strict nav filter).
      id: 'absence-insights',
      name: 'Absence Insights',
      icon: CalendarOff,
      path: '/absence-insights',
      position: 'main',
      group: 'people-operations',
      requiredPermission: 'space.people.absence_insights.view',
    },
    {
      // Confidential: company-wide attrition and probation figures. Hidden from everyone without
      // PEOPLE_COCKPIT_VIEW, which only the named overrides hold (see permissions.ts).
      // Its own section, People Analytics (David, Oct 9 2026): it was moved into People & Org
      // Planning by a merge commit, which put a page with its own audience into a section defined
      // by sharing one.
      id: 'people-cockpit',
      name: 'People Cockpit',
      icon: Gauge,
      path: '/people-cockpit',
      position: 'main',
      group: 'people-analytics',
      requiredPermission: PEOPLE_COCKPIT_VIEW,
    },
    {
      // Moved out of the "Testing Ground" grouping into its own "People & Org Planning" section
      // (Sep 23 2026) — this outgrew the sandbox framing once squad-org-planning@finn.com became
      // the real audience. Permission is unchanged: see permissions.ts#INTERNAL_PLAYGROUND_VIEW
      // and rbac.js#RBAC_GROUP_ORG_PLANNING_VIEWERS (the per-user allowlist is empty since
      // Oct 19 2026).
      id: 'hiring-capacity-timeline',
      name: 'Org Planning Overview',
      icon: FlaskConical,
      path: '/internal-playground/hiring-capacity-timeline',
      position: 'main',
      group: 'people-org-planning',
      // Either audience — the proposers (squad-org-planning-extended@finn.com, Oct 1 2026) too.
      requiredPermission: ORG_PLANNING_OVERVIEW_ACCESS,
    },
    {
      // AOP 2027 planning (Anna, Oct 2026): department leads add next year's hires as AOP drafts.
      // Same audience as the overview above — a department lead is a proposer, scoped to their line.
      id: 'aop-2027',
      name: 'AOP 2027 Planning',
      icon: CalendarRange,
      path: '/aop-2027',
      position: 'main',
      group: 'people-org-planning',
      requiredPermission: ORG_PLANNING_OVERVIEW_ACCESS,
    },
    {
      // Same permission as the Org Planning Overview above — deliberately, so the two pages share
      // one audience and one Workspace group (`org-planning-viewers`, mapped onto
      // squad-org-planning@finn.com). Read access only: who may actually approve a role is the
      // three named approvers, enforced per request in the proxy.
      // ⚠️ NOT the proposers: the overview above admits them, this page deliberately does not.
      id: 'role-approvals',
      name: 'Role Approvals',
      icon: ClipboardCheck,
      path: '/role-approvals',
      position: 'main',
      group: 'people-org-planning',
      requiredPermission: INTERNAL_PLAYGROUND_VIEW,
    },

  ],
};

export const sidebarGroups: SpaceNavGroup[] = [
  {
    id: 'boris',
    name: 'Boris, the Business Partner',
    icon: Bot,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'boris'),
  },
  {
    id: 'finnies',
    name: 'FINNies',
    icon: Users,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'finnies'),
  },
  {
    id: 'talent-acquisition',
    name: 'Talent Acquisition',
    icon: UserCheck,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'talent-acquisition'),
    subgroups: [
      // Every item in here is gated on CANDIDATE_COMMS_VIEW, so the whole subgroup now auto-hides
      // for anyone outside `cca.peoplespace@finn.com`. ⚠️ That is NEW as of Sep 23 2026: Usage
      // Analytics used to sit here ungated and was the one item that rendered this header for a
      // colleague outside the group. It moved to Product Experience below, so Candidate
      // Communication finally means what its name says. Talent Acquisition still shows the TA
      // Cockpit to everyone.
      { id: 'candidate-communication', name: 'Candidate Communication', icon: MessagesSquare },
      // Finding people who never applied and writing them a first InMail (Sofia, Oct 19 2026).
      // A folder inside Candidate Communication since Oct 23 2026 (Sofia). Every item is gated on
      // CANDIDATE_COMMS_VIEW, so it auto-hides the same way.
      { id: 'talent-sourcing', name: 'Talent Sourcing', icon: Send, parent: 'candidate-communication' },
      // How the product itself is doing: what people report about it (Feedback Triage) and what
      // they actually use (Usage Analytics). Deliberately NOT uniformly gated — Feedback Triage
      // carries CANDIDATE_COMMS_VIEW and Usage Analytics carries nothing, so a colleague outside
      // the group sees this header with Usage Analytics alone under it. That is the same
      // trade-off Candidate Communication used to make, and the reason that board stays ungated:
      // a tracking board nobody can reach must not look like a tracking board that is missing.
      { id: 'product-experience', name: 'Product Experience', icon: Gauge },
    ],
  },
  {
    id: 'people-operations',
    name: 'People Operations',
    icon: Building2,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'people-operations'),
  },
  {
    // People Cockpit (attrition, forward-looking attrition, probation). Every item carries its
    // own requiredPermission, so the section auto-hides for anyone who holds none of them.
    id: 'people-analytics',
    name: 'People Analytics',
    icon: ChartPie,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'people-analytics'),
  },
  {
    id: 'onboarding',
    name: 'Onboarding',
    icon: CalendarDays,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'onboarding'),
  },
  {
    id: 'offboarding',
    name: 'Offboarding',
    icon: UserMinus,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'offboarding'),
  },
  {
    // Renamed from "Testing Ground" (Sep 23 2026): its only two items, Org Planning Overview and
    // Role Approvals, share one real audience now (squad-org-planning@finn.com via the
    // `org-planning-viewers` RBAC group, plus core admins — see rbac.js; the per-user
    // INTERNAL_PLAYGROUND_ADMIN_EMAILS allowlist is empty since Oct 19 2026). Both items still
    // carry requiredPermission individually, so the whole section auto-hides for anyone outside
    // that audience — same "group disappears when empty" behavior as Candidate Communication's
    // subgroup above; there is no group-level gate.
    id: 'people-org-planning',
    name: 'People & Org Planning',
    icon: Network,
    items: peopleSpaceConfig.navItems.filter((i) => i.group === 'people-org-planning'),
  },
];
